import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import EnhancedModal from '../../../common/EnhancedModal/EnhancedModal';
import ModalCommonButtonsContainer from '../../../common/ModalComponent/ModalCommonButtonsContainer';
import ThreeDotsLoader from '../../../common/three-dots-loader/three-dots-loader';
import { claimChallengeReward, getChallengeRewardDetails } from '../../../../services/dashboard.service';
import { ChallengeRewardDetails, ChallengeShippingAddress } from '../../../../types/challenges';
import { isAddressLineOneValid } from '../../../../utils/validations';
import useIsMobile from '../../../../hooks/useIsMobile';
import SelectVariantsStep from './SelectVariantsStep';
import ShippingAddressStep from './ShippingAddressStep';
import styles from './claimRewardModal.module.css';

type ClaimRewardModalProps = {
  open: boolean;
  reviewId: string;
  address: any;
  onClose: () => void;
  onClaimed: () => void;
};

type ClaimStep = 'variants' | 'address';

type ShippingAddressForm = Required<Omit<ChallengeShippingAddress, 'phone'>>;

export default function ClaimRewardModal({ open, reviewId, address, onClose, onClaimed }: ClaimRewardModalProps) {
  const isMobile = useIsMobile();
  const [step, setStep] = useState<ClaimStep>('variants');
  const [rewardDetails, setRewardDetails] = useState<ChallengeRewardDetails | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isClaiming, setIsClaiming] = useState<boolean>(false);
  const [selectedVariants, setSelectedVariants] = useState<Record<number, number>>({});
  const [shippingAddress, setShippingAddress] = useState<ShippingAddressForm>({
    addressLineOne: address?.addressLineOne || '',
    addressLineTwo: address?.addressLineTwo || '',
    pincode: address?.pincode || '',
    city: address?.city || '',
    state: address?.state || '',
    country: address?.country || '',
  });

  const products = rewardDetails?.reward?.products || [];
  const hasSelectedAllVariants = products.length > 0 && products.every(({ productId }) => selectedVariants[productId]);
  const isAddressComplete =
    isAddressLineOneValid(shippingAddress.addressLineOne) &&
    ['pincode', 'city', 'state', 'country'].every((key) =>
      Boolean(shippingAddress[key as keyof ShippingAddressForm]?.trim())
    );

  const fetchRewardDetails = async () => {
    setIsLoading(true);
    const response = await getChallengeRewardDetails(reviewId);
    if (response?.status) {
      setRewardDetails(response.data);
      setSelectedVariants(
        response.data.reward.products.reduce<Record<number, number>>((acc, { productId, variants }) => {
          const availableVariants = variants.filter(({ available }) => available);
          if (availableVariants.length === 1) acc[productId] = availableVariants[0].variantId;
          return acc;
        }, {})
      );
    } else {
      toast.error(response?.message || 'Something went wrong');
      onClose();
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchRewardDetails();
  }, [reviewId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmVariants = () => {
    if (!hasSelectedAllVariants) {
      toast.error('Select variants to continue');
      return;
    }
    setStep('address');
  };

  const handleClaimReward = async () => {
    if (!isAddressComplete) {
      toast.error('Add your shipping address to continue');
      return;
    }
    setIsClaiming(true);
    const response = await claimChallengeReward(reviewId, {
      shippingAddress: {
        addressLineOne: shippingAddress.addressLineOne.trim(),
        ...(shippingAddress.addressLineTwo.trim() && { addressLineTwo: shippingAddress.addressLineTwo.trim() }),
        pincode: shippingAddress.pincode.trim(),
        city: shippingAddress.city.trim(),
        state: shippingAddress.state.trim(),
        country: shippingAddress.country.trim(),
      },
      products: products.map(({ productId }) => ({ productId, variantId: selectedVariants[productId] })),
    });
    setIsClaiming(false);
    if (response?.status) {
      if (response.data?.rewardStatus === 'failed') {
        toast.error(response.data.rewardFailureReason || 'Unable to claim reward');
        return;
      }
      toast.success('Reward claimed successfully');
      onClaimed();
    } else {
      toast.error(response?.message || 'Something went wrong');
    }
  };

  const isVariantsStep = step === 'variants';
  const modalWidth = isVariantsStep ? 637 : 496;
  const buttonText = (text: string) => <span className={styles.footerButtonText}>{text}</span>;

  const renderFooter = () => {
    if (isLoading) return null;
    if (isVariantsStep) {
      return (
        <ModalCommonButtonsContainer
          submitButtonText={buttonText('Confirm address')}
          cancelButtonText={buttonText('Cancel')}
          onClose={onClose}
          onSave={handleConfirmVariants}
          className={`${styles.footerButtons} ${isMobile ? 'p-4 flex flex-col-reverse' : 'p-6'}`}
          primaryButtonClassName={`${styles.footerButton} ${
            !hasSelectedAllVariants ? styles.footerButtonInactive : ''
          }`}
          secondaryButtonClassName={`${styles.footerButton} ${styles.cancelButton}`}
        />
      );
    }
    return (
      <ModalCommonButtonsContainer
        submitButtonText={buttonText('Claim reward')}
        cancelButtonText={buttonText('Go back')}
        onClose={() => setStep('variants')}
        onSave={handleClaimReward}
        loading={isClaiming}
        className={`${styles.footerButtons} ${isMobile ? 'p-4 flex flex-col-reverse' : `p-6 ${styles.addressFooter}`}`}
        primaryButtonClassName={`${styles.footerButton} ${!isAddressComplete ? styles.footerButtonInactive : ''}`}
        secondaryButtonClassName={`${styles.footerButton} ${styles.goBackButton}`}
      />
    );
  };

  return (
    <EnhancedModal
      open={open}
      onClose={onClose}
      title={isVariantsStep ? 'Select variants' : 'Confirm your shipping address'}
      subtitle={
        isVariantsStep && !isMobile
          ? `Pick the variants for your free product${products.length > 1 ? 's' : ''}.`
          : undefined
      }
      showBackButton={isVariantsStep || !isMobile}
      handleBack={isVariantsStep ? onClose : () => setStep('variants')}
      showCloseButton={!isMobile}
      width={modalWidth}
      maxWidth={isMobile ? 'calc(100vw - 32px)' : modalWidth}
      maxHeight={isMobile ? 'calc(100vh - 80px)' : 720}
      className={styles.claimRewardModal}
      headerClassName={styles.modalHeader}
      subTitleClassName={styles.modalSubtitle}
      bodyClassName={styles.modalBody}
      footerClassName={`p-0 ${isMobile || (isVariantsStep && products.length > 1) ? 'upper-shadow' : ''}`}
      footer={renderFooter()}
    >
      {isLoading && (
        <div className={styles.loaderContainer}>
          <ThreeDotsLoader />
        </div>
      )}
      {!isLoading && isVariantsStep && (
        <SelectVariantsStep
          products={products}
          selectedVariants={selectedVariants}
          onSelectVariant={(productId, variantId) =>
            setSelectedVariants((prev) => ({ ...prev, [productId]: variantId }))
          }
        />
      )}
      {!isLoading && !isVariantsStep && (
        <ShippingAddressStep address={shippingAddress} setAddress={setShippingAddress} />
      )}
    </EnhancedModal>
  );
}
